import { makeRequest } from './https';
import {
  CreateEmployeeTaskRequest,
  CreateMyTaskRequest,
  Task,
  TaskListOption,
  ThreadMessage,
  ThreadReplyRequest,
  UpdateEmployeeTaskRequest,
  UpdateMyTaskRequest,
} from '../types/task.type';

export interface TaskQueryParams {
  employee_id?: string;
  task_list_id?: string;
  linked_to_objective?: boolean;
  page?: number;
  limit?: number;
}

interface ListsResponse {
  data: TaskListOption[];
}

interface TasksResponse {
  data: Task[];
  errors?: string[];
}

export const getTasks = (params: TaskQueryParams = {}) =>
  makeRequest({ url: '/tasks', method: 'get', params }) as Promise<TasksResponse>;

export const getMyTasks = (params: Pick<TaskQueryParams, 'task_list_id'> = {}) =>
  makeRequest({ url: '/tasks/my', method: 'get', params }) as Promise<TasksResponse>;

export const getTaskLists = (employeeId: string) =>
  makeRequest({
    url: '/tasks/lists',
    method: 'get',
    params: { employee_id: employeeId },
  }) as Promise<ListsResponse>;

export const getMyTaskLists = () =>
  makeRequest({ url: '/tasks/my/lists', method: 'get' }) as Promise<ListsResponse>;

export const createTask = (body: CreateEmployeeTaskRequest) =>
  makeRequest({ url: '/tasks', method: 'post', data: body }) as Promise<Task>;

export const updateTask = (googleTaskId: string, body: UpdateEmployeeTaskRequest) =>
  makeRequest({ url: `/tasks/${googleTaskId}`, method: 'patch', data: body }) as Promise<Task>;

export const deleteTask = (task: Pick<Task, 'google_task_id' | 'employee_id' | 'task_list_id'>) =>
  makeRequest({
    url: `/tasks/${task.google_task_id}`,
    method: 'delete',
    params: { employee_id: task.employee_id, task_list_id: task.task_list_id },
  }) as Promise<{ message: string }>;

export const createMyTask = (body: CreateMyTaskRequest) =>
  makeRequest({ url: '/tasks/my', method: 'post', data: body }) as Promise<Task>;

export const updateMyTask = (googleTaskId: string, body: UpdateMyTaskRequest) =>
  makeRequest({ url: `/tasks/my/${googleTaskId}`, method: 'patch', data: body }) as Promise<Task>;

export const deleteMyTask = (task: Pick<Task, 'google_task_id' | 'task_list_id'>) =>
  makeRequest({
    url: `/tasks/my/${task.google_task_id}`,
    method: 'delete',
    params: { task_list_id: task.task_list_id },
  }) as Promise<{ message: string }>;

export const replyToThread = (body: ThreadReplyRequest) =>
  makeRequest({
    url: '/tasks/threads/reply',
    method: 'post',
    data: body,
  }) as Promise<ThreadMessage>;

export const syncTasks = () =>
  makeRequest({
    url: '/tasks/sync',
    method: 'post',
  }) as Promise<{ message: string }>;

let syncInFlight: Promise<{ message: string }> | null = null;

export function syncTasksOnce() {
  if (!syncInFlight) {
    syncInFlight = syncTasks().finally(() => {
      syncInFlight = null;
    });
  }
  return syncInFlight;
}

export interface SyncStatus {
  last_synced_at: string | null;
  last_active_at: string | null;
}

export const getSyncStatus = () =>
  makeRequest({
    url: '/tasks/sync-status',
    method: 'get',
  }) as Promise<SyncStatus>;
